import React from 'react';
import { CheckCircle, User } from 'lucide-react';
import { WeeklyStats } from '../types';

interface StatsProps {
  stats: WeeklyStats;
}

export const Stats: React.FC<StatsProps> = ({ stats }) => {
  const maryProgress = stats.mary.totalChores > 0 
    ? (stats.mary.completedChores / stats.mary.totalChores) * 100 
    : 0;
  
  const maxwellProgress = stats.maxwell.totalChores > 0 
    ? (stats.maxwell.completedChores / stats.maxwell.totalChores) * 100 
    : 0;

  const totalTime = stats.mary.totalTime + stats.maxwell.totalTime;
  const totalCompleted = stats.mary.completedChores + stats.maxwell.completedChores;
  const totalChores = stats.mary.totalChores + stats.maxwell.totalChores;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
      {/* Mary's Stats */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <User className="h-5 w-5 text-pink-500" />
            <h3 className="text-lg font-semibold text-gray-900">Mary</h3>
          </div>
          <div className="text-sm text-gray-500">
            {stats.mary.completedChores}/{stats.mary.totalChores} done
          </div>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Progress</span>
            <span className="text-sm font-medium text-gray-900">{maryProgress.toFixed(0)}%</span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-pink-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${maryProgress}%` }}
            ></div>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Time</span>
            <span className="font-medium text-gray-900">{stats.mary.totalTime} min</span>
          </div>
        </div>
      </div>

      {/* Maxwell's Stats */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <User className="h-5 w-5 text-blue-500" />
            <h3 className="text-lg font-semibold text-gray-900">Maxwell</h3>
          </div>
          <div className="text-sm text-gray-500">
            {stats.maxwell.completedChores}/{stats.maxwell.totalChores} done
          </div>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Progress</span>
            <span className="text-sm font-medium text-gray-900">{maxwellProgress.toFixed(0)}%</span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${maxwellProgress}%` }}
            ></div>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Time</span>
            <span className="font-medium text-gray-900">{stats.maxwell.totalTime} min</span>
          </div>
        </div>
      </div>

      {/* Overall Stats */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <h3 className="text-lg font-semibold text-gray-900">Overall</h3>
          </div>
          <div className="text-sm text-gray-500">
            {totalCompleted}/{totalChores} done
          </div>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Total Time</span>
            <span className="text-sm font-medium text-gray-900">{totalTime} min</span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Completion</span>
            <span className="text-sm font-medium text-gray-900">
              {totalChores > 0 ? ((totalCompleted / totalChores) * 100).toFixed(0) : 0}%
            </span>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Balance</span>
            <span className={`font-medium ${
              Math.abs(stats.mary.totalTime - stats.maxwell.totalTime) <= 30 
                ? 'text-green-600' 
                : 'text-yellow-600'
            }`}>
              {Math.abs(stats.mary.totalTime - stats.maxwell.totalTime)} min diff
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}; 